import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  VStack,
  Icon,
  Spinner,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { useDisclosure } from '@chakra-ui/react';
import {
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
} from '@/components/ui/menu';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FiMoreVertical,
  FiSearch,
  FiRefreshCw,
  FiPlus,
  FiRotateCcw,
  FiLogOut,
  FiTrash2,
} from 'react-icons/fi';
import { RiWhatsappLine } from 'react-icons/ri';
import { useState, useEffect, useMemo } from 'react';
import { useGetList, useNotify, useRefresh } from 'react-admin';
import { WhatsAppChannelTable } from './channel-whatsapp-table';
import { AddWhatsAppChannelModal } from './channel-add-whatsapp-modal';
import { useWhatsAppChannel } from '@/hooks/WAHA/useWhatsapp';
import { Channel } from '@/features/channels/channels.types';
import { deleteChannel } from '@/features/channels/channels.service';
import { useQueryClient } from '@tanstack/react-query';

type WhatsAppOverviewProps = {
  canCreateChannels?: boolean;
};

export function WhatsAppOverview({
  canCreateChannels = true,
}: WhatsAppOverviewProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();

  const whatsAppHook = useWhatsAppChannel();

  const { data: phoneNumbersData, isLoading: loadingPhoneNumbers } = useGetList(
    'channel-phone-numbers',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'id', order: 'ASC' },
    },
  );

  const phoneNumberMap = useMemo(() => {
    if (!phoneNumbersData) return {};
    return (phoneNumbersData || []).reduce((map, item) => {
      map[item.channel_id] = item.phone_number;
      return map;
    }, {});
  }, [phoneNumbersData]);

  const {
    data: channelsData,
    isLoading,
    error,
  } = useGetList<Channel>('channels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: { type: 'whatsapp' },
  });

  useEffect(() => {
    if (error) {
      notify('Error fetching channels', { type: 'warning' });
      console.error('Error fetching channels:', error);
    }
  }, [error, notify]);

  const filteredChannels = useMemo(() => {
    const whatsAppChannels =
      channelsData?.filter((ch) => ch.type === 'whatsapp') ?? [];
    if (!searchQuery) {
      return whatsAppChannels;
    }
    return whatsAppChannels.filter(
      (channel) =>
        channel.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (phoneNumberMap[channel.id] &&
          phoneNumberMap[channel.id].includes(searchQuery)),
    );
  }, [channelsData, searchQuery, phoneNumberMap]);

  const handleAddChannel = () => {
    if (!canCreateChannels) {
      toaster.create({
        title: 'Insufficient Permissions',
        description: 'Only admins can create new channels.',
        type: 'warning',
      });
      return;
    }
    setSelectedChannel(null);
    onOpen();
  };

  const handleChannelUpdate = (updatedChannel: Channel) => {
    refresh();
    onClose();
    toaster.create({
      title: selectedChannel ? 'Channel Updated' : 'Channel Added',
      description: `WhatsApp channel ${updatedChannel.name} has been processed.`,
      type: 'success',
    });
  };

  const handleStatusClick = (channel: Channel) => {
    if (
      channel.status?.toUpperCase() === 'SCAN_QR_CODE' ||
      channel.status?.toUpperCase() === 'INITIALIZING' ||
      channel.status?.toUpperCase() === 'PENDING' ||
      channel.status?.toUpperCase() === 'FAILED'
    ) {
      setSelectedChannel(channel);
      onOpen();
    } else {
      toaster.create({
        title: 'Channel Status',
        description: `Channel "${channel.name}" status: ${channel.status}`,
        type: 'info',
      });
    }
  };

  // Add state to track transitioning channels
  const [transitioningChannels, setTransitioningChannels] = useState<
    Set<string>
  >(new Set());

  // Update the start handler
  const handleStartChannel = async (channelId: string) => {
    try {
      // Mark channel as transitioning
      setTransitioningChannels((prev) => new Set([...prev, channelId]));

      // Call the API
      await whatsAppHook.startSession(channelId);

      // Immediately check status to update UI
      await whatsAppHook.syncSessionStatus(channelId);

      toaster.create({
        title: 'Channel Started',
        description: 'WhatsApp channel is starting...',
        type: 'success',
      });
    } catch (err) {
      console.error('Failed to start channel:', err);
      toaster.create({
        title: 'Error Starting Channel',
        description:
          err instanceof Error ? err.message : 'Could not start channel.',
        type: 'error',
      });
    } finally {
      // Clear transitioning state
      setTransitioningChannels((prev) => {
        const updated = new Set([...prev]);
        updated.delete(channelId);
        return updated;
      });
    }
  };

  // Update the stop handler similarly
  const handleStopChannel = async (channelId: string) => {
    try {
      setTransitioningChannels((prev) => new Set([...prev, channelId]));

      await whatsAppHook.stopSession(channelId);

      // Try to sync status but don't fail if it errors
      try {
        await whatsAppHook.syncSessionStatus(channelId);
      } catch (syncErr) {
        console.warn('Failed to sync status after stop:', syncErr);
      }

      // Refresh the channel list
      refresh();

      toaster.create({
        title: 'Channel Stopped',
        description: 'WhatsApp channel has been stopped',
        type: 'success',
      });
    } catch (err) {
      console.error('Failed to stop channel:', err);
      toaster.create({
        title: 'Error Stopping Channel',
        description:
          err instanceof Error ? err.message : 'Could not stop channel.',
        type: 'error',
      });
    } finally {
      setTransitioningChannels((prev) => {
        const updated = new Set([...prev]);
        updated.delete(channelId);
        return updated;
      });
    }
  };

  // State for confirming destructive actions
  const [actionChannel, setActionChannel] = useState<{
    id: string;
    name: string;
    action: string;
  } | null>(null);
  const {
    open: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();

  // Handle channel actions with confirmation for destructive ones
  const handleChannelAction = (channel: Channel, action: string) => {
    const sessionId = getSessionIdFromChannel(channel);
    if (!sessionId) {
      toaster.create({
        title: 'Action Failed',
        description: 'Could not find session ID for this channel',
        type: 'error',
      });
      return;
    }

    // For non-destructive actions, execute immediately
    if (action === 'restart') {
      handleRestartChannel(sessionId, channel.name);
    }
    // For destructive actions, ask for confirmation
    else if (['logout', 'delete'].includes(action)) {
      setActionChannel({ id: sessionId, name: channel.name, action });
      onConfirmOpen();
    }
  };

  // Helper to extract session ID from channel config
  const getSessionIdFromChannel = (channel: Channel) => {
    try {
      if (typeof channel.channel_config === 'string') {
        const config = JSON.parse(channel.channel_config);
        return config.sessionId || config.whatsapp?.sessionName;
      } else if (channel.channel_config) {
        return (
          channel.channel_config.sessionId ||
          (channel.channel_config.whatsapp &&
            channel.channel_config.whatsapp.sessionName)
        );
      }
    } catch (err) {
      console.error('Error parsing channel config:', err);
    }
    return null;
  };

  // Execute confirmed action
  const executeConfirmedAction = async () => {
    if (!actionChannel) return;

    const { id: sessionId, name: channelName, action } = actionChannel;

    try {
      if (action === 'logout') {
        await whatsAppHook.logoutSession(sessionId);
        toaster.create({
          title: 'Session Logged Out',
          description: `${channelName} has been logged out.`,
          type: 'success',
        });
      } else if (action === 'delete') {
        // Try to delete from WAHA first (may fail if session doesn't exist)
        try {
          await whatsAppHook.deleteSession(sessionId);
        } catch (wahaErr) {
          console.warn(`WAHA session delete failed (may not exist): ${wahaErr}`);
          // Continue to delete from DB anyway
        }

        // Delete from database
        await deleteChannel(sessionId);

        toaster.create({
          title: 'Channel Deleted',
          description: `${channelName} has been deleted.`,
          type: 'success',
        });
        refresh(); // Refresh the list to remove the deleted channel
      }
    } catch (err) {
      console.error(`Failed to ${action} channel:`, err);
      toaster.create({
        title: `Error Executing ${action}`,
        description:
          err instanceof Error ? err.message : `Could not ${action} channel.`,
        type: 'error',
      });
    } finally {
      onConfirmClose();
      setActionChannel(null);
    }
  };

  // Additional handlers for session actions
  const handleRestartChannel = async (
    sessionId: string,
    channelName: string,
  ) => {
    try {
      await whatsAppHook.restartSession(sessionId);
      toaster.create({
        title: 'Restarting Channel',
        description: `${channelName} is being restarted...`,
        type: 'info',
      });
    } catch (err) {
      console.error('Failed to restart channel:', err);
      toaster.create({
        title: 'Error Restarting Channel',
        description:
          err instanceof Error ? err.message : 'Could not restart channel.',
        type: 'error',
      });
    }
  };

  // Function to customize WhatsAppChannelTable to add more actions
  const renderChannelActions = (channel: Channel) => (
    <MenuRoot>
      <MenuTrigger asChild>
        <IconButton aria-label="More options" variant="ghost" size="sm">
          <FiMoreVertical />
        </IconButton>
      </MenuTrigger>
      <MenuContent>
        <MenuItem
          value="restart"
          onClick={() => handleChannelAction(channel, 'restart')}
        >
          <FiRotateCcw />
          Restart
        </MenuItem>
        <MenuItem
          value="logout"
          onClick={() => handleChannelAction(channel, 'logout')}
        >
          <FiLogOut />
          Logout
        </MenuItem>
        <MenuItem
          value="delete"
          onClick={() => handleChannelAction(channel, 'delete')}
        >
          <FiTrash2 />
          Delete
        </MenuItem>
      </MenuContent>
    </MenuRoot>
  );

  // Replace the existing effect with a more optimized version
  useEffect(() => {
    let isMounted = true;
    let syncTimerId: number | undefined;

    // Load data once on mount with small delay to prevent race conditions
    const initialLoadTimerId = setTimeout(() => {
      if (!isMounted) return;

      // Only sync if no sync is in progress and data is stale
      const lastSyncTime =
        (queryClient.getQueryData(['lastStatusSyncTime']) as number) || 0;
      const now = Date.now();

      if (!whatsAppHook.isSyncingStatus && now - lastSyncTime > 60000) {
        // 1 minute
        whatsAppHook.syncAllSessionStatuses().catch((err) => {
          console.error('Failed to sync statuses:', err);
        });
      }

      // Set up interval for periodic sync with backoff on errors
      let consecutiveErrors = 0;

      syncTimerId = window.setInterval(() => {
        if (!isMounted || whatsAppHook.isSyncingStatus) return;

        whatsAppHook
          .syncAllSessionStatuses()
          .then(() => {
            consecutiveErrors = 0; // Reset error count on success
          })
          .catch((err) => {
            console.error('Failed to sync statuses:', err);
            consecutiveErrors++;

            // If we have multiple consecutive errors, back off by clearing the interval
            if (consecutiveErrors >= 3 && syncTimerId) {
              console.warn('Too many sync errors, stopping automatic sync');
              clearInterval(syncTimerId);
            }
          });
      }, 300000); // Every 5 minutes
    }, 5000); // 5 second initial delay

    return () => {
      isMounted = false;
      clearTimeout(initialLoadTimerId);
      if (syncTimerId) clearInterval(syncTimerId);
    };
  }, [whatsAppHook]);

  // Modify the manual sync button to prevent spam clicks
  const [syncButtonDisabled, setSyncButtonDisabled] = useState(false);

  const handleManualSync = () => {
    if (syncButtonDisabled || whatsAppHook.isSyncingStatus) return;

    setSyncButtonDisabled(true);
    whatsAppHook
      .syncAllSessionStatuses()
      .then(() => {
        toaster.create({
          title: 'Status Synced',
          description: 'Channel statuses synced with WhatsApp server',
          type: 'success',
        });
      })
      .catch((err) => {
        console.error('Failed to sync statuses:', err);
        toaster.create({
          title: 'Sync Failed',
          description: 'Failed to sync with WhatsApp server',
          type: 'error',
        });
      })
      .finally(() => {
        // Prevent spam clicking by disabling for 3 seconds
        setTimeout(() => setSyncButtonDisabled(false), 3000);
      });
  };

  if (isLoading || loadingPhoneNumbers) {
    return (
      <Box w="full" display="flex" justifyContent="center" py={12}>
        <Spinner size="xl" color="green.500" />
      </Box>
    );
  }

  return (
    <Box w="full" height="100%" display="flex" flexDirection="column">
      <Flex justify="space-between" mb={6} gap={4} flexShrink={0}>
        <HStack
          width="100%"
          bg="gray.100"
          borderRadius="xl"
          px={4}
          py={2}
          position="relative"
          height="40px"
          alignItems="center"
        >
          <Input
            placeholder="Search WhatsApp channels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fontSize="md"
            border="none"
            flex="1"
            _focus={{
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
            }}
            _focusVisible={{
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
            }}
            _placeholder={{
              color: 'gray.500',
            }}
          />
          <IconButton
            aria-label="Search"
            size="sm"
            type="submit"
            variant="ghost"
            color="gray.500"
            borderRadius="full"
            _hover={{ bg: 'gray.200' }}
          >
            <Icon as={FiSearch} />
          </IconButton>
        </HStack>
        <Flex gap={3}>
          <Tooltip content="Sync status with WhatsApp server">
            <IconButton
              aria-label="Sync statuses"
              onClick={handleManualSync}
              loading={whatsAppHook.isSyncingStatus}
              disabled={syncButtonDisabled}
              variant="outline"
              size="md"
              borderRadius="xl"
              bg="gray.50"
              borderColor="gray.300"
              color="gray.600"
              _hover={{
                bg: 'gray.100',
                borderColor: 'gray.400',
                color: 'gray.700',
              }}
              _active={{
                bg: 'gray.200',
                borderColor: 'gray.500',
              }}
              _disabled={{
                opacity: 0.5,
                cursor: 'not-allowed',
                _hover: {
                  bg: 'gray.50',
                  borderColor: 'gray.300',
                },
              }}
              transition="all 0.2s"
            >
              <FiRefreshCw />
            </IconButton>
          </Tooltip>
          {canCreateChannels ? (
            <Button
              onClick={handleAddChannel}
              variant="solid"
              size="md"
              borderRadius="xl"
              bg="green.500"
              color="white"
              _hover={{
                bg: 'green.600',
                transform: 'translateY(-1px)',
                boxShadow: 'lg',
              }}
              _active={{
                bg: 'green.700',
                transform: 'translateY(0)',
              }}
              transition="all 0.2s"
              fontWeight="semibold"
              px={6}
            >
              <FiPlus />
              Add Channel
            </Button>
          ) : (
            <Tooltip content="Only admins can create channels">
              <Button
                variant="outline"
                size="md"
                borderRadius="xl"
                px={6}
                isDisabled
              >
                <FiPlus />
                Add Channel
              </Button>
            </Tooltip>
          )}
        </Flex>
      </Flex>

      {!isLoading && filteredChannels.length === 0 ? (
        <VStack gap={4} py={12} textAlign="center">
          <Icon as={RiWhatsappLine} boxSize={12} color="gray.400" />
          <Text color="gray.500" fontSize="lg">
            {channelsData && channelsData.length > 0
              ? 'No channels match your search.'
              : 'No WhatsApp channels found.'}
          </Text>
          {!(channelsData && channelsData.length > 0) && canCreateChannels && (
            <Button colorPalette="green" onClick={handleAddChannel}>
              Add Your First WhatsApp Channel
            </Button>
          )}
        </VStack>
      ) : (
        <Box flex="1" height="100%" overflow="hidden">
          <WhatsAppChannelTable
            channels={filteredChannels.map((channel) => ({
              ...channel,
              phone: phoneNumberMap[channel.id] || '-',
            }))}
            onStatusClick={handleStatusClick}
            onStartChannel={handleStartChannel}
            onStopChannel={handleStopChannel}
            renderActions={renderChannelActions}
            transitioningChannels={[...transitioningChannels]}
          />
        </Box>
      )}

      <AddWhatsAppChannelModal
        isOpen={isOpen}
        onClose={onClose}
        selectedChannel={selectedChannel}
        onChannelCreate={handleChannelUpdate}
      />

      {/* Add confirmation dialog */}
      <DialogRoot
        open={isConfirmOpen}
        onOpenChange={onConfirmClose}
        closeOnInteractOutside={false}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {actionChannel?.action === 'logout'
                ? 'Logout WhatsApp Channel'
                : 'Delete WhatsApp Channel'}
            </DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>
          <DialogBody>
            {actionChannel?.action === 'logout'
              ? `Are you sure you want to logout from ${actionChannel?.name}? This will require re-scanning the QR code to reconnect.`
              : `Are you sure you want to delete ${actionChannel?.name}? This action cannot be undone.`}
          </DialogBody>
          <DialogFooter>
            <DialogCloseTrigger>
              <Button variant="outline">Cancel</Button>
            </DialogCloseTrigger>
            <Button colorPalette="red" onClick={executeConfirmedAction}>
              {actionChannel?.action === 'logout' ? 'Logout' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  );
}
